/**
 * Phase 8-G — KPI strip for the legacy dashboard.
 *
 * Renders four tiles pulled from the agency stats endpoint:
 *   - Approved (in window)
 *   - Avg time to approve  (p50 of time_to_approve_ms)
 *   - Auto-approval rate
 *   - Active employees
 *
 * Window is fixed to `7d` to match the "this week" framing — for the deep-dive
 * the user is pointed at `/stats` via the "Detailed stats" link in the
 * header, which already supports window switching. (`/agency/stats` still
 * resolves for inbound links; both paths render the same page.)
 *
 * Trend visuals use a tiny inline bar (the same chart-less approach the
 * agency-portal Stats view uses). No new chart deps.
 *
 * Wrapped in `<RoleGate capability="stats.view">` by the caller.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SimpleCard from '../../ui/cards/SimpleCard';
import {
  StatsResponse,
  getAgencyStats,
} from '../../lib/api/stats/stats-api';
import '../../styles/pages/dashboard/dashboardAgencyCards.scss';

const formatMs = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const formatPercent = (rate: number | null | undefined): string => {
  if (rate == null || !Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
};

interface TileProps {
  label: string;
  value: string;
  trendLabel?: string;
  /** 0.0 – 1.0 — fills the inline bar. Omit to hide the bar. */
  trendFill?: number;
}

const StatsTile = ({ label, value, trendLabel, trendFill }: TileProps) => {
  const safeFill =
    trendFill == null
      ? null
      : Math.max(0, Math.min(1, trendFill));
  return (
    <div className='dashboardStatsStrip__tile'>
      <div className='dashboardStatsStrip__tile-label'>{label}</div>
      <div className='dashboardStatsStrip__tile-value'>{value}</div>
      {trendLabel != null && (
        <div className='dashboardStatsStrip__tile-trend'>
          {safeFill != null && (
            <div className='dashboardStatsStrip__tile-trend-bar'>
              <div
                className='dashboardStatsStrip__tile-trend-bar-fill'
                style={{ width: `${safeFill * 100}%` }}
              />
            </div>
          )}
          <span>{trendLabel}</span>
        </div>
      )}
    </div>
  );
};

const DashboardStatsStrip = () => {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getAgencyStats('7d');
        if (!cancelled) setData(res);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[DashboardStatsStrip] load failed', err);
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load stats.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const approved = data?.summary?.proposals_approved ?? 0;
  const total = data?.summary?.proposals_total ?? 0;
  const approvalShare = total > 0 ? approved / total : 0;
  const autoRate = data?.summary?.auto_approval_rate ?? 0;
  const activeEmployees = data?.summary?.active_employees ?? 0;
  const p50 = data?.time_to_approve_ms?.p50 ?? null;

  return (
    <SimpleCard grow={false}>
      <div className='dashboardStatsStrip'>
        <div className='dashboardStatsStrip__header'>
          <h3>This week</h3>
          <Link
            to='/stats'
            className='pendingProposalsCard__header-link'
          >
            Detailed stats
          </Link>
        </div>

        {loading && (
          <div className='dashboardStatsStrip__loading'>Loading…</div>
        )}

        {!loading && error && (
          <div className='dashboardStatsStrip__error'>
            Could not load stats.
          </div>
        )}

        {!loading && !error && (
          <div className='dashboardStatsStrip__tiles'>
            <StatsTile
              label='Approved this week'
              value={approved.toLocaleString()}
              trendLabel={
                total > 0
                  ? `${approved} of ${total} proposals`
                  : 'No proposals yet'
              }
              trendFill={approvalShare}
            />
            <StatsTile
              label='Avg time to approve'
              value={formatMs(p50)}
              trendLabel='Median (p50)'
            />
            <StatsTile
              label='Auto-approval rate'
              value={formatPercent(autoRate)}
              trendLabel='Auto vs reviewed'
              trendFill={autoRate}
            />
            <StatsTile
              label='Active employees'
              value={activeEmployees.toLocaleString()}
              trendLabel='With activity in window'
            />
          </div>
        )}
      </div>
    </SimpleCard>
  );
};

export default DashboardStatsStrip;
